// `availableFilters`, and the filter definitions' `inputType`/`min`/`placeholder`, are
// editor-only: the API and the evaluator never see them. A rule written by a script, an
// integration or a generated campaign carries none of them, so the editor recovers them
// from the catalogue the form already holds instead of reading them back off the rule.

export const resolveAvailableFilters = (rule, availableRules = []) => {
  if (rule.availableFilters) {
    return rule.availableFilters;
  }

  const sameKey = availableRules.filter((template) => template.key === rule.key);
  // `priceIncrease` names both the event rule and the product-attributes rule, so the key
  // alone would hand one of them the other's filters.
  const template = sameKey.length > 1 ? sameKey.find((candidate) => candidate.type === rule.type) : sameKey[0];

  return template?.availableFilters ?? {};
};

const EDITOR_ONLY_FILTER_KEYS = ['inputType', 'min', 'max', 'step', 'placeholder', 'required'];

// `timestamp` has three definitions - days ago, hours ago and absolute - told apart only
// by their conditionType.
const pickDefinition = (candidates, filter) => {
  const sameField = candidates.filter((definition) => definition.field === filter.field);

  return sameField.length > 1
    ? sameField.find((candidate) => candidate.conditionType === filter.conditionType)
    : sameField[0];
};

export const resolveFilter = (filter, filterDefinitions, ruleAvailableFilters = {}) => {
  if (!filter) {
    return filter;
  }

  // The form builds a filter as `{ ...filters[key], ...ruleAvailableFilters[key] }`, and a
  // rule may override `field` there (`subscriptions` becomes `custom.string.subscriptions`),
  // so the rule's own offering is searched first. It also settles fields two global
  // definitions share, such as `tags` or `custom.numeric.points`.
  const ruleDefinitions = Object.entries(ruleAvailableFilters).map(([key, override]) => ({
    ...filterDefinitions[key],
    ...override,
  }));
  const definition =
    pickDefinition(ruleDefinitions, filter) ?? pickDefinition(Object.values(filterDefinitions), filter);

  if (!definition) {
    return filter;
  }

  // Only the editor-only keys come back: the rest of a definition (`aggregation`, default
  // `values`) is evaluator-facing, and RuleFilter hands this object to `updateFilter`, so
  // merging it would be saved on the next edit and change the segment.
  const editorFields = { name: definition.name };
  EDITOR_ONLY_FILTER_KEYS.forEach((key) => {
    if (definition[key] !== undefined) {
      editorFields[key] = definition[key];
    }
  });

  return { ...editorFields, ...filter };
};
